import React from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';

/**
 * Composant Modal d'Alerte Réutilisable
 * Remplace les alert() natifs par des modals élégantes
 */
const AlertModal = ({ 
  isOpen, 
  onClose, 
  type = 'info',
  title,
  message,
  confirmText = 'OK',
  onConfirm,
  showCancel = false,
  cancelText = 'Annuler'
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Configuration des styles selon le type
  const configs = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-500',
      buttonColor: 'bg-green-600 hover:bg-green-700',
      titleColor: 'text-green-900'
    },
    error: {
      icon: XCircle,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      buttonColor: 'bg-red-600 hover:bg-red-700',
      titleColor: 'text-red-900'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-500',
      buttonColor: 'bg-amber-600 hover:bg-amber-700',
      titleColor: 'text-amber-900'
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-500',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      titleColor: 'text-blue-900'
    }
  };

  const config = configs[type] || configs.info;
  const Icon = config.icon;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[10000]"
      onClick={handleCancel}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4 mb-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full ${config.bgColor} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          
          <div className="flex-1 pt-1">
            {title && (
              <h3 className={`text-lg font-semibold ${config.titleColor} mb-2`}>
                {title}
              </h3>
            )}
          </div>
        </div>

        <div className={`${config.bgColor} border-l-4 ${config.borderColor} p-4 rounded-r-lg mb-6`}>
          <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
            {message}
          </div>
        </div>

        <div className={`flex gap-3 ${showCancel ? 'justify-end' : 'justify-center'}`}>
          {showCancel && (
            <button
              onClick={handleCancel}
              className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              {cancelText}
            </button>
          )}
          
          <button
            onClick={handleConfirm}
            className={`px-6 py-2.5 ${config.buttonColor} text-white rounded-lg font-medium transition-colors shadow-sm ${
              !showCancel ? 'min-w-[120px]' : ''
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;